import React, { useRef, useEffect } from 'react';
import { Form, Alert, InputGroup, Button } from 'react-bootstrap';
import { FaPlus, FaMinus, FaSpinner } from 'react-icons/fa';

export default function PrescriptionTab({
    editingNote,
    noteForm,
    isAssistant,
    isNoteDateToday,
    isNoteDateInFuture,
    showAdvancedOptions,
    setShowAdvancedOptions,
    diagnosis,
    setDiagnosis,
    treatments,
    isLoadingUniqueTreatments,
    uniqueTreatments,
    loadingTreatments,
    openDropdownIndex,
    setOpenDropdownIndex,
    treatmentRefs,
    handleAddTreatment,
    handleRemoveTreatment,
    handleTreatmentChange,
}) {
    const textareaRefs = useRef({});
    
    const canEditPrescription = ((!editingNote && isNoteDateToday() && !isAssistant) || (editingNote && (noteForm.type === 'treatment' || noteForm.type === 'appointment') && !isNoteDateInFuture() && !isAssistant));
    
    // Auto-expand textareas when treatments change (from loading or suggestions)
    useEffect(() => {
        treatments.forEach((treatment, index) => {
            const textarea = textareaRefs.current[index];
            if (textarea) {
                textarea.style.height = 'auto';
                textarea.style.height = textarea.scrollHeight + 'px';
            }
        });
    }, [treatments]);

    if (!canEditPrescription) {
        return <Alert variant="info">Prescription is available on the appointment day or later.</Alert>;
    }

    return (
        <>
            <div className="border-bottom pb-2 mb-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Prescription Details</h5>
                <Button variant="link" onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className="p-0">
                    {showAdvancedOptions ? 'Hide Advanced Options' : 'Show Advanced Options'}
                </Button>
            </div>

            <div className={`advanced-options ${showAdvancedOptions ? 'd-block' : 'd-none'}`}>
                <Form.Group className="mb-3">
                    <Form.Label>Diagnosis</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={diagnosis}
                        onChange={(e) => setDiagnosis(e.target.value)}
                        placeholder="Enter diagnosis details..."
                        disabled={loadingTreatments}
                    />
                </Form.Group>
            </div>

            <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="mb-0">Treatments</Form.Label>
                    <Button variant="outline-primary" size="sm" onClick={handleAddTreatment} type="button">
                        <FaPlus className="me-1" /> Add Treatment
                    </Button>
                </div>
                {isLoadingUniqueTreatments && (
                    <div className="text-muted small mb-2">Loading treatment suggestions...</div>
                )}
                {loadingTreatments ? (
                    <div className="text-center"><FaSpinner className="fa-spin" /> Loading treatments...</div>
                ) : (
                    <div className="mb-2">
                        {treatments.map((treatment, index) => {
                            const filter = (treatment || '').trim().toLowerCase();
                            const filtered = (uniqueTreatments || [])
                                .filter(s => !filter || (s && s.toLowerCase().includes(filter)))
                                .slice(0, 10);

                            return (
                                <div
                                    key={index}
                                    className="mb-3 position-relative"
                                    ref={(el) => { treatmentRefs.current[index] = el; }}
                                >
                                    <InputGroup>
                                        <Form.Control
                                            as="textarea"
                                            rows={1}
                                            value={treatment}
                                            ref={(el) => { textareaRefs.current[index] = el; }}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                handleTreatmentChange(index, val);
                                                const hasFilter = (val || '').trim().length > 0;
                                                setOpenDropdownIndex(hasFilter ? index : null);
                                                
                                                // Auto-expand textarea
                                                e.target.style.height = 'auto';
                                                e.target.style.height = e.target.scrollHeight + 'px';
                                            }}
                                            placeholder="Enter treatment details..."
                                            autoComplete="off"
                                            onFocus={() => {
                                                const hasFilter = (treatment || '').trim().length > 0;
                                                if (hasFilter) setOpenDropdownIndex(index);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Escape') setOpenDropdownIndex(null);
                                            }}
                                            style={{ resize: 'none', overflow: 'hidden', minHeight: '38px' }}
                                        />
                                        <Button
                                            variant="outline-danger"
                                            onClick={() => handleRemoveTreatment(index)}
                                            disabled={treatments.length <= 1}
                                            type="button"
                                            title="Remove treatment"
                                        >
                                            <FaMinus />
                                        </Button>
                                    </InputGroup>

                                    {openDropdownIndex === index && filter && filtered.length > 0 && (
                                        <div
                                            className="list-group position-absolute w-100 shadow-sm"
                                            style={{ zIndex: 1050, maxHeight: '180px', overflowY: 'auto' }}
                                        >
                                            {filtered.map((s, i) => (
                                                <button
                                                    key={i}
                                                    type="button"
                                                    className="list-group-item list-group-item-action py-1"
                                                    onClick={() => { handleTreatmentChange(index, s); setOpenDropdownIndex(null); }}
                                                    title={s}
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Form.Group>
        </>
    );
}
